import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Calendar, Pill, FlaskConical, FileText, Stethoscope, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import FileUpload from "@/components/FileUpload";

function StatRow({ label, value, icon: Icon }) {
  return (
    <div className="flex items-center gap-3 p-4 bg-muted/40 rounded-lg">
      <Icon className="size-5 text-primary" />
      <div>
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="text-lg font-bold">{value}</div>
      </div>
    </div>
  );
}

export default function PatientPortal() {
  const { user } = useAuth();
  const [doctor, setDoctor] = useState(null);
  const [appts, setAppts] = useState([]);
  const [rx, setRx] = useState([]);
  const [labs, setLabs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date_raw: "", reason: "" });

  const load = useCallback(async () => {
    const [u, a, r, l] = await Promise.all([
      api.get("/users"),
      api.get("/appointments"),
      api.get("/prescriptions"),
      api.get("/lab-requests"),
    ]);
    setDoctor(u.data[0] || null);
    setAppts(a.data);
    setRx(r.data);
    setLabs(l.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const book = async (e) => {
    e.preventDefault();
    try {
      await api.post("/appointments", {
        patient_id: "auto", doctor_id: "auto",
        date: new Date(form.date_raw).toISOString(),
        duration_min: 30, reason: form.reason, type: "in_person",
      });
      toast.success("Booking request submitted");
      setOpen(false); setForm({ date_raw: "", reason: "" });
      load();
    } catch {
      toast.error("Could not book");
    }
  };

  const upcoming = appts.filter((a) => new Date(a.date) > new Date()).slice(0, 5);
  const past = appts.filter((a) => new Date(a.date) <= new Date()).slice(0, 5);

  return (
    <div className="space-y-6 fade-up" data-testid="patient-portal">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">My health</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">Welcome, {user.name?.split(" ")[0]}</h1>
        <p className="text-sm text-muted-foreground mt-1">Your private patient portal with {doctor?.name || "your doctor"}.</p>
      </div>

      {/* My doctor */}
      {doctor && (
        <Card>
          <CardContent className="p-6 flex flex-col sm:flex-row gap-4 sm:items-center">
            <Avatar className="size-16">
              <AvatarFallback className="bg-primary text-primary-foreground text-lg font-bold">
                {doctor.name?.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Your doctor</div>
              <div className="font-bold text-xl mt-0.5">{doctor.name}</div>
              <div className="text-sm text-muted-foreground flex items-center gap-1.5 mt-0.5">
                <Stethoscope className="size-3.5" /> {doctor.specialty || "General Practice"}
              </div>
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="patient-book-btn">
                  <CalendarPlus className="size-4" /> Book appointment
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Book appointment</DialogTitle></DialogHeader>
                <form onSubmit={book} className="space-y-3" data-testid="patient-book-form">
                  <div className="text-sm text-muted-foreground">With {doctor.name}</div>
                  <div className="space-y-1.5"><Label>Date & time *</Label>
                    <Input type="datetime-local" required value={form.date_raw} onChange={(e) => setForm({ ...form, date_raw: e.target.value })} data-testid="patient-book-date" /></div>
                  <div className="space-y-1.5"><Label>Reason</Label>
                    <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Consultation, follow-up..." data-testid="patient-book-reason" /></div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button type="submit">Request</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatRow label="Upcoming visits" value={upcoming.length} icon={Calendar} />
        <StatRow label="Active Rx" value={rx.length} icon={Pill} />
        <StatRow label="Lab results" value={labs.length} icon={FlaskConical} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card>
          <CardHeader><CardTitle className="text-base">Upcoming appointments</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {upcoming.map((a) => (
              <div key={a.id} className="flex items-center gap-3 p-3 border border-border rounded-lg">
                <Calendar className="size-4 text-primary" />
                <div className="flex-1">
                  <div className="font-medium text-sm">{new Date(a.date).toLocaleString()}</div>
                  <div className="text-xs text-muted-foreground">{a.reason || "Consultation"}</div>
                </div>
                <Badge variant="outline">{a.status}</Badge>
              </div>
            ))}
            {upcoming.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">No upcoming appointments.</div>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Recent prescriptions</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {rx.slice(0, 3).map((r) => (
              <div key={r.id} className="p-3 border border-border rounded-lg">
                <div className="flex justify-between text-[11px] text-muted-foreground mono">
                  <span>{new Date(r.created_at).toLocaleDateString()}</span>
                  <span>{r.doctor_name}</span>
                </div>
                <ul className="mt-1.5 space-y-0.5">
                  {r.items?.map((it, i) => (
                    <li key={`${r.id}-${i}-${it.medicine}`} className="text-sm">• <strong>{it.medicine}</strong> — {it.dosage}, {it.duration}</li>
                  ))}
                </ul>
              </div>
            ))}
            {rx.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">No prescriptions yet.</div>}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="text-base">Past visits</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {past.map((a) => (
              <div key={a.id} className="flex items-center gap-3 p-3 border border-border rounded-lg">
                <FileText className="size-4 text-muted-foreground" />
                <div className="flex-1">
                  <div className="font-medium text-sm">{new Date(a.date).toLocaleString()}</div>
                  <div className="text-xs text-muted-foreground">{a.reason || "Consultation"}</div>
                </div>
                <Badge variant="outline">{a.status}</Badge>
              </div>
            ))}
            {past.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">No past visits.</div>}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="text-base">My files</CardTitle></CardHeader>
          <CardContent>
            <FileUpload patientId={user.patient_id} resourceType="general" label="Share files with your doctor (PDFs, images, scans)" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
